import { reactive } from 'vue'
import { apiFetch } from './api'

export interface Ticket {
  id: string
  title: string
  category: string
  priority: 'critica' | 'alta' | 'media' | 'baixa'
  description: string
  projectId: string
  taskId: string
  toolItemId: string
  location: string
  desiredDueDate: string
  requesterUsername: string
  assignedRoleIds: string[]
  assignedToUsername: string
  status: string
  createdAt: string
  updatedAt: string
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

function mapTicket(value: unknown): Ticket {
  const row = asRecord(value)
  return {
    id: String(row.id || ''),
    title: String(row.title || ''),
    category: String(row.category || 'outros'),
    priority:
      row.priority === 'critica' || row.priority === 'alta' || row.priority === 'baixa'
        ? row.priority
        : 'media',
    description: String(row.description || ''),
    projectId: String(row.projectId || ''),
    taskId: String(row.taskId || ''),
    toolItemId: String(row.toolItemId || ''),
    location: String(row.location || ''),
    desiredDueDate: String(row.desiredDueDate || ''),
    requesterUsername: String(row.requesterUsername || ''),
    assignedRoleIds: Array.isArray(row.assignedRoleIds)
      ? row.assignedRoleIds.filter((item): item is string => typeof item === 'string')
      : [],
    assignedToUsername: String(row.assignedToUsername || ''),
    status: String(row.status || 'novo'),
    createdAt: String(row.createdAt || ''),
    updatedAt: String(row.updatedAt || '')
  }
}

const state = reactive({
  items: [] as Ticket[],
  loading: false
})

export const ticketsStore = {
  state,
  all() {
    return state.items
  },
  async refresh(filters?: { status?: string; category?: string; projectId?: string; priority?: string }) {
    state.loading = true
    try {
      const params = new URLSearchParams()
      if (filters?.status) params.set('status', filters.status)
      if (filters?.category) params.set('category', filters.category)
      if (filters?.projectId) params.set('projectId', filters.projectId)
      if (filters?.priority) params.set('priority', filters.priority)
      const suffix = params.toString() ? `?${params.toString()}` : ''
      const rows = await apiFetch<unknown[]>(`/tickets${suffix}`)
      state.items = rows.map(mapTicket)
      return state.items
    } finally {
      state.loading = false
    }
  },
  async create(payload: {
    title: string
    category: string
    priority: 'critica' | 'alta' | 'media' | 'baixa'
    description: string
    projectId?: string
    taskId?: string
    toolItemId?: string
    location?: string
    desiredDueDate?: string
  }) {
    await apiFetch<{ ok: boolean; id: string }>('/tickets', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
    await this.refresh()
  },
  async claim(id: string) {
    await apiFetch<{ ok: boolean }>(`/tickets/${encodeURIComponent(id)}/claim`, {
      method: 'POST',
      body: JSON.stringify({})
    })
    await this.refresh()
  },
  async setStatus(id: string, status: string, notes = '') {
    await apiFetch<{ ok: boolean }>(`/tickets/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, notes })
    })
    await this.refresh()
  }
}
